import { useCallback, useEffect, useState } from 'react';
import {
  analyseLine,
  type Customer,
  type Finding,
  type Proposal,
  type Station,
} from '@visionqc/shared';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';
import { fmtTime } from '../lib/format';
import {
  AddDeviceWizard,
  FindingRow,
  LineSheet,
  ROLE_COLOUR,
  ROLE_ICON,
  ROLE_LABEL,
  type DeviceProfile,
  type LineDevice,
} from './LineView';

/**
 * Proposals: a line designed for a customer who does not have one yet.
 *
 * Deliberately built from the same components as a running line. A quote drawn
 * by different code than the machine is a quote that stops describing it, and
 * the customer is the one holding the stale copy.
 *
 * The order on screen is the order the conversation happens in: what they
 * asked for, what that line would have to be, what it costs, and only then
 * whether it actually meets the request. That last one is the part worth
 * paying for — a proposal that quietly ignores a throughput the line cannot
 * reach is a proposal that becomes an argument at commissioning.
 */

export interface Design {
  id: number;
  customerId: number;
  customer: string | null;
  name: string;
  request: string | null;
  line: {
    beltSpeedMmS: number | null;
    partLengthMm: number | null;
    partGapMm: number | null;
    inspectionMs: number | null;
    inspectionWorstMs: number | null;
    mmPerPulse: number | null;
  };
  status: string;
  stationId: number | null;
  hourlyRate: number | null;
  discountPct: number;
  currency: string;
  notes: string | null;
  quoteRef: string | null;
  quotedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS: Record<string, { label: string; badge: string }> = {
  draft: { label: 'Draft', badge: '' },
  sent: { label: 'Sent', badge: 'unsure' },
  won: { label: 'Won', badge: 'good' },
  lost: { label: 'Lost', badge: 'bad' },
};

export function DesignsView() {
  const isAdmin = useIsAdmin();
  const [designs, setDesigns] = useState<Design[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([
      api.listDesigns().catch(() => []),
      api.listCustomers().catch(() => []),
    ]);
    setDesigns(d);
    setCustomers(c);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Proposals</h1>
        <div className="panel muted">Preparing proposals requires the ADMIN role.</div>
      </div>
    );
  }

  if (open !== null) {
    return (
      <DesignDetail
        id={open}
        onBack={() => {
          setOpen(null);
          void load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Proposals</h1>
        <button className="btn primary" onClick={() => setCreating((v) => !v)}>
          {creating ? '✕ Cancel' : '+ New proposal'}
        </button>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      {creating && (
        <NewDesign
          customers={customers}
          onCancel={() => setCreating(false)}
          onCreated={async (id) => {
            setCreating(false);
            await load();
            setOpen(id);
          }}
          onError={setError}
        />
      )}

      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>Proposal</th>
              <th>Customer</th>
              <th>Line</th>
              <th>Status</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {designs.map((d) => {
              const st = STATUS[d.status] ?? STATUS.draft!;
              return (
                <tr key={d.id}>
                  <td>
                    <button
                      style={{
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        color: 'var(--accent)',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                      onClick={() => setOpen(d.id)}
                    >
                      {d.name}
                    </button>
                    {d.request && (
                      <div className="muted" style={{ fontSize: 11, maxWidth: 380 }}>
                        {d.request.slice(0, 90)}
                        {d.request.length > 90 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td className="muted">{d.customer}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {d.line.beltSpeedMmS ? `${d.line.beltSpeedMmS} mm/s` : '—'}
                    {d.line.partLengthMm ? ` · ${d.line.partLengthMm} mm parts` : ''}
                  </td>
                  <td>
                    <span className={`badge ${st.badge}`}>{st.label}</span>
                    {d.stationId && <span className="muted" style={{ fontSize: 11 }}> · built</span>}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmtTime(d.updatedAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn sm" onClick={() => setOpen(d.id)}>
                      →
                    </button>
                  </td>
                </tr>
              );
            })}
            {designs.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No proposals yet. Start one from a customer's request.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewDesign({
  customers,
  onCreated,
  onCancel,
  onError,
}: {
  customers: Customer[];
  onCreated: (id: number) => void | Promise<void>;
  onCancel: () => void;
  onError: (m: string) => void;
}) {
  const [customerId, setCustomerId] = useState<number | null>(customers[0]?.id ?? null);
  const [name, setName] = useState('');
  const [request, setRequest] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <div className="panel" style={{ marginBottom: 20, borderLeft: '4px solid var(--accent)' }}>
      <strong>New proposal</strong>
      <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 240px' }}>
          <label>Customer</label>
          <select
            value={customerId ?? ''}
            onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}
            style={{ width: '100%' }}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label>Name</label>
          <input
            autoFocus
            value={name}
            placeholder="e.g. Bolt line 1"
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label>What did they ask for?</label>
        <textarea
          rows={3}
          value={request}
          placeholder="In their words — e.g. 120 bolts/min, sort scratches and dents into separate bins"
          onChange={(e) => setRequest(e.target.value)}
          style={{ width: '100%' }}
        />
        {/* Kept verbatim because the line parameters below are our READING of
            this, and the two are worth comparing when a proposal is questioned
            months later. */}
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          Kept as written. The numbers you enter next are your reading of it.
        </div>
      </div>
      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="btn primary"
          disabled={!name.trim() || !customerId || busy}
          onClick={async () => {
            setBusy(true);
            try {
              const d = await api.createDesign({
                customerId,
                name: name.trim(),
                request: request.trim() || null,
              });
              await onCreated(d.id);
            } catch (e) {
              onError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Create
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function DesignDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const [design, setDesign] = useState<Design | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [devices, setDevices] = useState<LineDevice[]>([]);
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ key: string; note: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const [p, d, prof, st] = await Promise.all([
      api.designProposal(id).catch(() => null),
      api.listLineDevices(undefined, id).catch(() => []),
      api.listDeviceProfiles().catch(() => []),
      api.listStations().catch(() => []),
    ]);
    if (p) {
      setDesign(p.design);
      setProposal(p.proposal);
    }
    setDevices(d);
    setProfiles(prof);
    setStations(st);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!design || !proposal) return <div className="spinner">Loading proposal…</div>;

  const L = design.line;
  const val = (k: string, fallback: number | null) =>
    form[k] ?? (fallback === null ? '' : String(fallback));

  // The analysis runs on the design's own numbers, so the answer on screen is
  // the answer for THIS line rather than a default.
  const analysis =
    L.beltSpeedMmS && L.partLengthMm
      ? analyseLine(
          {
            speedMmS: L.beltSpeedMmS,
            partLengthMm: L.partLengthMm,
            gapMm: L.partGapMm ?? 0,
            inspectionMs: L.inspectionMs ?? 520,
            inspectionWorstMs: L.inspectionWorstMs ?? undefined,
            mmPerPulse: L.mmPerPulse ?? undefined,
          },
          devices.map((d) => ({
            name: d.name,
            role: d.role,
            positionMm: d.positionMm,
            actuationMs: d.actuationMs,
            dwellMs: d.dwellMs,
          })),
        )
      : null;

  const save = async (patch: Record<string, unknown>) => {
    setBusy(true);
    try {
      await api.updateDesign(id, patch);
      setForm({});
      await load();
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const money = (v: number) => `${v.toFixed(2)} ${proposal.currency}`;
  const num = (key: string, label: string, current: number | null, hint?: string) => (
    <div style={{ flex: '0 0 140px' }}>
      <label>{label}</label>
      <input
        value={val(key, current)}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        onBlur={(e) => {
          const v = e.target.value === '' ? null : Number(e.target.value);
          if (v !== current) void save({ [key]: v });
        }}
        style={{ width: '100%' }}
      />
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );

  const st = STATUS[design.status] ?? STATUS.draft!;

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <button className="btn sm" onClick={onBack}>
            ← All proposals
          </button>
          <h1 className="view-title" style={{ marginTop: 8 }}>
            {design.name}
            <span className="muted" style={{ fontWeight: 400, fontSize: 15 }}>
              {' '}
              · {design.customer}
            </span>{' '}
            <span className={`badge ${st.badge}`}>{st.label}</span>
          </h1>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      {/* Shown once and unrecoverable, so it gets its own unmissable panel. */}
      {newKey && (
        <div className="panel" style={{ marginBottom: 16, borderLeft: '4px solid var(--warn)' }}>
          <strong>Station key</strong>
          <p className="muted" style={{ fontSize: 13, margin: '6px 0' }}>
            {newKey.note}
          </p>
          <code
            style={{
              display: 'block',
              padding: 10,
              background: 'var(--panel-2)',
              borderRadius: 8,
              wordBreak: 'break-all',
            }}
          >
            {newKey.key}
          </code>
          <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setNewKey(null)}>
            I've stored it
          </button>
        </div>
      )}

      {design.request && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <strong style={{ fontSize: 13 }}>What they asked for</strong>
          <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{design.request}</p>
        </div>
      )}

      <div className="panel" style={{ marginBottom: 16 }}>
        <strong>The line this would need</strong>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {num('beltSpeedMmS', 'Belt speed', L.beltSpeedMmS, 'mm/s')}
          {num('partLengthMm', 'Part length', L.partLengthMm, 'mm')}
          {num('partGapMm', 'Gap', L.partGapMm, 'mm')}
          {num('inspectionMs', 'Inspection', L.inspectionMs, 'ms typical')}
          {num('inspectionWorstMs', 'Worst case', L.inspectionWorstMs, 'ms')}
          {num('mmPerPulse', 'Encoder', L.mmPerPulse, 'mm/pulse')}
        </div>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {num('hourlyRate', 'Hourly rate', design.hourlyRate, proposal.currency)}
          {num('discountPct', 'Discount', design.discountPct, '%')}
        </div>
      </div>

      {analysis && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <strong>Does it meet the request?</strong>
          <div className="row" style={{ gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
            <Figure label="Throughput" value={`${analysis.throughputPerMin.toFixed(0)}/min`} />
            <Figure
              label="Rejector no closer than"
              value={`${analysis.minRejectDistanceWorstMm.toFixed(0)} mm`}
              hint="from the camera, worst case"
            />
          </div>
          {/* The part worth paying for. A proposal that ignores a throughput the
              line cannot reach becomes an argument at commissioning. */}
          {analysis.findings.map((f, i) => (
            <FindingRow key={i} finding={f} />
          ))}
          {analysis.placements.map((p) =>
            p.findings.map((f, i) => <FindingRow key={`${p.name}${i}`} finding={f} where={p.name} />),
          )}
        </div>
      )}

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>Equipment</strong>
          <button className="btn sm" onClick={() => setAdding((v) => !v)}>
            {adding ? '✕ Cancel' : '+ Add device'}
          </button>
        </div>

        {adding && (
          <div style={{ marginTop: 12 }}>
            <AddDeviceWizard
              profiles={profiles}
              stations={stations}
              designId={id}
              onCancel={() => setAdding(false)}
              onAdded={async (name) => {
                setAdding(false);
                setNotice(`${name} added to the proposal.`);
                await load();
              }}
              onError={setError}
            />
          </div>
        )}

        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Equipment</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Unit</th>
              <th style={{ textAlign: 'right' }}>Install</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {proposal.items.map((i, k) => (
              <tr key={k}>
                <td>
                  <span style={{ color: ROLE_COLOUR[i.role] ?? 'var(--text-dim)' }}>
                    {ROLE_ICON[i.role] ?? '▪'}
                  </span>{' '}
                  <strong>{i.name}</strong>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {ROLE_LABEL[i.role] ?? i.role}
                    {i.positionMm !== null ? ` · ${i.positionMm} mm` : ''}
                  </div>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {i.brand ? `${i.brand} ${i.model}` : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{i.quantity}</td>
                <td style={{ textAlign: 'right' }} className="muted">
                  {i.unitPrice === null ? (
                    <span style={{ color: 'var(--warn)' }}>no price</span>
                  ) : (
                    money(i.unitPrice)
                  )}
                </td>
                <td style={{ textAlign: 'right' }} className="muted">
                  {i.installHours ? `${i.installHours * i.quantity} h` : '—'}
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {i.lineTotal === null ? '—' : money(i.lineTotal)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn sm"
                    disabled={design.status === 'won'}
                    title={design.status === 'won' ? 'This line was built' : 'Remove'}
                    onClick={async () => {
                      const dev = devices.find((x) => x.name === i.name);
                      if (dev) {
                        await api.deleteLineDevice(dev.id);
                        await load();
                      }
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ textAlign: 'right' }} className="muted">
                Equipment
              </td>
              <td style={{ textAlign: 'right' }}>{money(proposal.equipmentTotal)}</td>
              <td />
            </tr>
            <tr>
              <td colSpan={5} style={{ textAlign: 'right' }} className="muted">
                Installation ({proposal.installHours} h)
              </td>
              <td style={{ textAlign: 'right' }}>{money(proposal.labourTotal)}</td>
              <td />
            </tr>
            {proposal.discount > 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'right' }} className="muted">
                  Discount {design.discountPct}%
                </td>
                <td style={{ textAlign: 'right' }}>−{money(proposal.discount)}</td>
                <td />
              </tr>
            )}
            <tr>
              <td colSpan={5} style={{ textAlign: 'right' }}>
                <strong>Total</strong>
              </td>
              <td style={{ textAlign: 'right' }}>
                <strong>{money(proposal.total)}</strong>
              </td>
              <td />
            </tr>
          </tfoot>
        </table>

        {proposal.items.length === 0 && (
          // A total of 0.00 with nothing above it reads as a bug. Say what it
          // actually means.
          <FindingRow
            finding={{
              severity: 'warning',
              message: 'Nothing is on this proposal yet, which is why the total is zero.',
              fix: 'Add the camera, the trigger and whatever acts on the verdict.',
            } as Finding}
          />
        )}
        {proposal.warnings.map((w, i) => (
          <FindingRow key={i} finding={{ severity: 'warning', message: w } as Finding} />
        ))}
      </div>

      {design.quoteRef && <QuotePanel designId={id} quoteRef={design.quoteRef} />}

      <LineSheet designId={id} reloadKey={devices.length} />

      <div className="panel" style={{ marginTop: 16 }}>
        <strong>Next step</strong>
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          {design.status === 'draft' && (
            <button
              className="btn primary"
              disabled={busy || proposal.items.length === 0}
              title={
                proposal.items.length === 0
                  ? 'Add equipment first — there is nothing to quote'
                  : 'Create the quote document in the invoicing system'
              }
              onClick={async () => {
                if (
                  !window.confirm(
                    'Send this proposal as a quote?\n\n' +
                      'It is created as a DRAFT — you assign the number in the invoicing ' +
                      'system. Prices are frozen now, so the document cannot drift from ' +
                      'what the customer is holding.',
                  )
                ) {
                  return;
                }
                setBusy(true);
                try {
                  const r = await api.quoteDesign(id);
                  await load();
                  setNotice(
                    `Quote ${(r.quote as { number?: string })?.number ?? ''} created with ` +
                      `${r.lines} lines.` +
                      (r.unpriced.length ? ` Not included: ${r.unpriced.join(', ')}.` : ''),
                  );
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Send as quote →
            </button>
          )}
          {design.status === 'sent' && (
            <>
              <button
                className="btn primary"
                disabled={busy}
                onClick={async () => {
                  const stationName = window.prompt(
                    'Name for the station this becomes:',
                    `${design.customer} — ${design.name}`,
                  );
                  if (!stationName) return;
                  setBusy(true);
                  try {
                    const r = await api.buildDesign(id, stationName);
                    setNewKey({ key: r.key, note: r.note });
                    setNotice(`Station created, ${r.devicesMoved} devices moved onto it.`);
                    await load();
                  } catch (e) {
                    setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Won — build it
              </button>
              <button
                className="btn"
                disabled={busy}
                onClick={() => void save({ status: 'lost' })}
              >
                Lost
              </button>
            </>
          )}
          {design.status === 'won' && design.stationId && (
            <span className="muted" style={{ fontSize: 13 }}>
              Built as station #{design.stationId}. Its devices now live on the Line page.
            </span>
          )}
          {design.status === 'lost' && (
            <span className="muted" style={{ fontSize: 13 }}>
              Marked lost. Kept on purpose — a lost proposal is the most useful thing to read
              before writing the next one.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && (
        <div className="muted" style={{ fontSize: 11 }}>
          {hint}
        </div>
      )}
    </div>
  );
}


/**
 * The quote document, read in place.
 *
 * Proxied through this app's backend rather than linked out to. A link means a
 * second login and a tenant switcher that lands on whichever tenant was last
 * used — which is how an operator ends up looking at someone else's books. The
 * service token never reaches the browser either way.
 */
function QuotePanel({ designId, quoteRef }: { designId: number; quoteRef: string }) {
  const [quote, setQuote] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    void api
      .designQuote(designId)
      .then(setQuote)
      .catch((e) => setError((e as Error).message));
  }, [designId]);

  return (
    <div className="panel" style={{ marginTop: 16, borderLeft: '4px solid var(--accent)' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>
          Quote {quote ? String(quote.number) : `#${quoteRef}`}{' '}
          {quote && (
            <span className={`badge ${quote.isDraft ? 'unsure' : 'good'}`}>
              {String(quote.status)}
            </span>
          )}
        </strong>
        <div className="row">
          <button className="btn sm" onClick={() => setShowPdf((v) => !v)}>
            {showPdf ? 'Hide PDF' : 'Show PDF'}
          </button>
          <a
            className="btn sm"
            href={api.designQuotePdfUrl(designId)}
            download={`quote-${quote ? String(quote.number) : quoteRef}.pdf`}
          >
            Download
          </a>
        </div>
      </div>

      {error && (
        <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          ⚠ {error}
        </p>
      )}

      {quote && (
        <>
          <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            {String((quote.customer as Record<string, string>)?.companyName ?? '')}
            {quote.validUntil ? ` · valid until ${String(quote.validUntil).slice(0, 10)}` : ''}
            {' · '}
            {Number(quote.totalTtc ?? 0).toFixed(2)} {String(quote.currency ?? '')}
          </p>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            A draft until you assign its number in the invoicing system — that step is
            irreversible, so it stays there.
          </p>
        </>
      )}

      {showPdf && (
        <iframe
          title="Quote"
          src={api.designQuotePdfUrl(designId)}
          style={{
            width: '100%',
            height: 700,
            marginTop: 14,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: '#fff',
          }}
        />
      )}
    </div>
  );
}
